import { createRoute, Link, redirect, useNavigate, useSearch } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { rootRoute } from './root.js';
import { useAuth } from '../auth/AuthContext.js';
import { isAuthenticated } from '../auth/AuthContext.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';

// Search-param schema for /login. Two callers populate `reset`:
//   - /reset/request → /login (after user requests a link and clicks
//     "Back to sign in") — not currently set, but reserved.
//   - /reset (confirm) → /login?reset=success on a successful reset.
const LoginSearchSchema = z.object({
  next: z.string().optional(),
  reset: z.enum(['success']).optional(),
});

/**
 * Login form schema — mirrors apps/api/src/routes/auth.ts LoginBody.
 * We DO want client-side validation for fast feedback, but the server
 * remains the source of truth; any schema drift shows up as 400 errors
 * the form handles generically.
 */
const LoginForm = z.object({
  orgSlug: z
    .string()
    .min(3)
    .max(63)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Slug is lowercase letters, digits, hyphens'),
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Password is required').max(128),
});
type LoginForm = z.infer<typeof LoginForm>;

/**
 * Where to land after login. Honours ?next= (so a deep link that bounced to
 * login — e.g. /billing — completes the round-trip) but ONLY for a same-origin
 * absolute path: must start with a single "/" (rejects "//host" protocol-relative
 * and full URLs), else fall back to the dashboard. No open-redirect.
 */
function safeNextPath(next: string | undefined): string {
  if (next && next.startsWith('/') && !next.startsWith('//')) return next;
  return '/dashboard';
}

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search) => LoginSearchSchema.parse(search),
  // Already authenticated → skip the form and honour ?next= (a deep link that
  // bounced here), else the dashboard. Previously hard-coded /dashboard, which
  // silently dropped `next` and stranded deep links (e.g. /billing).
  beforeLoad: ({ search }) => {
    if (isAuthenticated()) {
      throw redirect({ to: safeNextPath(search.next) });
    }
  },
  component: LoginPage,
});

function LoginPage(): JSX.Element {
  const { login } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ from: loginRoute.id });
  const [serverError, setServerError] = useState<string | null>(null);
  const showResetSuccess = search.reset === 'success';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(LoginForm),
    defaultValues: { orgSlug: '', email: '', password: '' },
  });

  async function onSubmit(values: LoginForm): Promise<void> {
    setServerError(null);
    try {
      const loggedIn = await login(values);
      // Role-based landing. Employees go straight to their self-service portal
      // (they can't reach admin routes, so ?next= is ignored for them); everyone
      // else honours ?next= (the deep link that bounced here) or the dashboard.
      // Admin routes have their own role guards, so a typed URL can't bypass this.
      const target = loggedIn.role === 'employee' ? '/me' : safeNextPath(search.next);
      await navigate({ to: target });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setServerError('Invalid credentials. Check org slug, email, and password.');
      } else if (err instanceof ApiError && err.status === 429) {
        setServerError('Too many attempts. Wait a minute and try again.');
      } else {
        setServerError('Login failed. Try again, or contact your admin if it persists.');
      }
    }
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-black text-white">
      <div className="w-full max-w-sm space-y-8 p-8">
        <header className="text-center space-y-2">
          <h1 className="font-serif text-3xl">LumoHR</h1>
          <p className="text-sm text-gray-400">Sign in to your workspace</p>
        </header>

        {showResetSuccess ? (
          <div
            role="status"
            className="rounded border border-green-900 bg-green-950/40 p-3 text-sm text-green-300 text-center"
          >
            Password reset. Sign in with your new password.
          </div>
        ) : null}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <Field
            id="orgSlug"
            label="Organization slug"
            placeholder="my-company"
            autoComplete="organization"
            error={errors.orgSlug?.message}
            inputProps={register('orgSlug')}
          />
          <Field
            id="email"
            label="Email"
            type="email"
            placeholder="you@company.com"
            autoComplete="email"
            error={errors.email?.message}
            inputProps={register('email')}
          />
          <Field
            id="password"
            label="Password"
            type="password"
            autoComplete="current-password"
            error={errors.password?.message}
            inputProps={register('password')}
          />

          {serverError ? <Alert variant="error">{serverError}</Alert> : null}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded bg-white text-black font-medium py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <p className="text-center text-sm text-gray-400">
          <Link to="/reset/request" className="underline hover:text-white">
            Forgot your password?
          </Link>
        </p>

        <p className="text-center text-sm text-gray-400">
          New to LumoHR?{' '}
          <Link to="/register" className="underline hover:text-white">
            Create a workspace
          </Link>
        </p>

        <p className="text-center text-xs text-gray-600">
          <Link to="/legal/data-retention" className="underline hover:text-gray-300">
            Data Retention &amp; Deletion Policy
          </Link>
        </p>
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Field — small labeled input with inline error
// ---------------------------------------------------------------------------

interface FieldProps {
  id: string;
  label: string;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  error?: string;
  inputProps: ReturnType<ReturnType<typeof useForm<LoginForm>>['register']>;
}

function Field({ id, label, type = 'text', placeholder, autoComplete, error, inputProps }: FieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs uppercase tracking-wide text-gray-400">
        {label}
      </label>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-invalid={error ? 'true' : 'false'}
        className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white placeholder-gray-600 focus:border-white focus:outline-none"
        {...inputProps}
      />
      {error ? <p className="text-xs text-red-400">{error}</p> : null}
    </div>
  );
}
